/**
 * Centralized date/time layer for the whole monorepo (frontend + backend).
 *
 * The project and its server run in the German timezone (Europe/Berlin). To avoid values
 * silently shifting across the frontend↔backend boundary, ALL date handling should go through
 * this module instead of using `moment`, `date-fns`, or raw `Date`/`.toISOString()`.
 *
 * Two kinds of temporal values — pick the right one:
 *
 *  1. CALENDAR DATE (a wall-calendar day, no time-of-day meaning): due dates, order dates,
 *     valid_from, gueltig_ab/bis, birth dates, history dates, etc.
 *       - Transport as a plain "YYYY-MM-DD" string. NEVER run it through `.toISOString()` —
 *         that converts Berlin-midnight to the *previous day* at 23:00 UTC (the classic
 *         "date goes back a day" bug).
 *       - Serialize with `toApiDate`, parse with `parseApiDate`, display with `formatDate`.
 *
 *  2. INSTANT (an exact point in time): createdAt, *At audit fields, log timestamps, etc.
 *       - Transport as a UTC ISO string (`toApiInstant`), display in Berlin (`formatDateTime`).
 *
 * Calendar helpers operate on the value's wall-clock fields (no timezone conversion), so a date
 * picked in a date picker round-trips to the same day. Instant helpers explicitly project onto
 * Europe/Berlin so a stored UTC instant always displays in German local time regardless of the
 * host's own timezone. On the backend the runtime TZ is additionally pinned to Europe/Berlin
 * (see apps/backend/set-env.js) so native `Date` agrees with this layer.
 */
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import isBetween from "dayjs/plugin/isBetween.js";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import minMax from "dayjs/plugin/minMax.js";
import quarterOfYear from "dayjs/plugin/quarterOfYear.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";
import "dayjs/locale/de.js";
// dayjs resolves to a single hoisted copy in this monorepo, so extending it here configures the
// same singleton used by the apps and by MUI's AdapterDayjs (locale + plugins applied globally).
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(minMax);
dayjs.extend(quarterOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
/** The application timezone. Everything date-related is anchored to this. */
export const APP_TZ = "Europe/Berlin";
dayjs.locale("de");
dayjs.tz.setDefault(APP_TZ);
/** Canonical wire format for calendar dates (date-only, no timezone). */
export const API_DATE_FORMAT = "YYYY-MM-DD";
/** Default human display format for calendar dates (German). */
export const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";
/** Default human display format for instants (German). */
export const DISPLAY_DATETIME_FORMAT = "DD.MM.YYYY HH:mm";
/** Formats accepted when parsing an incoming calendar date string. */
const CALENDAR_PARSE_FORMATS = ["YYYY-MM-DD", "DD.MM.YYYY"];
/** True for `null`, `undefined`, or `""` — the "no date" inputs call sites guard against. */
function isEmpty(input) {
    return input === null || input === undefined || input === "";
}
// ---------------------------------------------------------------------------
// Calendar dates (date-only)
// ---------------------------------------------------------------------------
/**
 * Serialize a calendar date to the canonical "YYYY-MM-DD" wire string, reading the value's
 * wall-clock fields (no timezone conversion). Use for any date-only field sent to the API.
 * Returns `null` for empty/invalid input. This REPLACES `.toISOString()` on date-only values.
 */
export function toApiDate(input) {
    if (isEmpty(input))
        return null;
    const d = dayjs(input);
    return d.isValid() ? d.format(API_DATE_FORMAT) : null;
}
/**
 * Parse an incoming calendar date ("YYYY-MM-DD", tolerant of "DD.MM.YYYY") into a Dayjs at
 * local midnight of that calendar day. Pass already-Date/Dayjs values through unchanged.
 */
export function parseApiDate(input) {
    if (typeof input === "string") {
        return dayjs(input, CALENDAR_PARSE_FORMATS, true).isValid()
            ? dayjs(input, CALENDAR_PARSE_FORMATS, true)
            : dayjs(input);
    }
    return dayjs(input);
}
/**
 * Today's calendar date as "YYYY-MM-DD" (in Berlin), wherever the code runs. `dayjs()` alone
 * would give the host's day: `setDefault` above only applies to `dayjs.tz()`.
 */
export function todayApiDate() {
    return dayjs().tz(APP_TZ).format(API_DATE_FORMAT);
}
/**
 * Format a calendar date for display (German), default "DD.MM.YYYY". Reads wall-clock fields,
 * no timezone conversion. Returns "" for empty/invalid input.
 */
export function formatDate(input, fmt = DISPLAY_DATE_FORMAT) {
    if (isEmpty(input))
        return "";
    const d = dayjs(input);
    return d.isValid() ? d.format(fmt) : "";
}
// ---------------------------------------------------------------------------
// Instants (exact points in time)
// ---------------------------------------------------------------------------
/**
 * Serialize an instant to a UTC ISO string (equivalent to `Date#toISOString`). Use for true
 * timestamps only (createdAt, *At audit fields, logs). Returns `null` for empty/invalid input.
 */
export function toApiInstant(input) {
    if (isEmpty(input))
        return null;
    const d = dayjs(input);
    return d.isValid() ? d.toISOString() : null;
}
/** Current instant as a UTC ISO string (equivalent to `new Date().toISOString()`). */
export function nowInstant() {
    return dayjs().toISOString();
}
/** Parse an instant (ISO) and project it onto Berlin local time. */
export function parseInstant(input) {
    return dayjs(input).tz(APP_TZ);
}
/**
 * Format an instant for display in Berlin (German), default "DD.MM.YYYY HH:mm". Projects onto
 * Europe/Berlin so the result is correct regardless of the host timezone. Returns "" for
 * empty/invalid input.
 */
export function formatDateTime(input, fmt = DISPLAY_DATETIME_FORMAT) {
    if (isEmpty(input))
        return "";
    const d = dayjs(input);
    return d.isValid() ? d.tz(APP_TZ).format(fmt) : "";
}
/** "now" as a Dayjs in Berlin local time. */
export function nowBerlin() {
    return dayjs().tz(APP_TZ);
}
/**
 * The configured dayjs singleton (German locale, Europe/Berlin default, all plugins extended).
 * Use this as a drop-in for the old `moment` import when a helper above doesn't fit; prefer the
 * named helpers for anything crossing the API boundary.
 */
export { dayjs };
export default dayjs;
